<template>
  <div class="card-update-tags">
    <card title="Edit Tags">

      <p>
        <info-box>
          <ul>
            <li>Click on the Add Tag button to create a tag</li>
            <li>Reposition tags by clicking and dragging</li>
            <li>Click on a tag to display the edit tag dialog box</li>
          </ul>
        </info-box>
      </p>

      <button class="btn btn-secondary my-2" @click="addTag">add tag</button>

      <div v-if="tagRef.length > 0" class="my-2">
        <label>Change slider position</label>
        <toggle-button 
          v-model="page.verticalSlider" 
          :color="{checked: '#28a745', unchecked: '#28a745'}"
          :sync="true" 
          :labels="{checked: 'Vertical', unchecked: 'Horizontal'}"
          :width="100" 
          :height="30" 
        />
      </div>

      <div class="tags-block">
        <div 
          class="canvas-wrapper" 
          ref="canvasWrapper"
          @mousemove="handleDrag($event)"
          @mouseup.left.stop="handleDragEnd($event)"
          :style="canvasStyle"
        >
          <tag-basic 
            v-for="tag in tagBasic" 
            :key="tag._id"
            v-bind="tag"
            :lang="lang"
            :class="{
              active: selectedTag._id === tag._id, 
              inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
            }"
            @mousedown.native.left.stop="handleDragStart($event, tag._id)"
            @mouseup.native.left.stop="handleDragEnd($event, tag._id)"
          ></tag-basic>

          <tag-box
            v-for="tag in tagBox"
            :key="tag._id"
            v-bind="tag"
            :lang="lang"
            :class="{
              active: selectedTag._id === tag._id, 
              inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
            }"
            @mousedown.native.left.stop="handleDragStart($event, tag._id)"
            @mouseup.native.left.stop="handleDragEnd($event, tag._id)"
          ></tag-box>

          <tag-reference
            v-for="(tag, index) in tagRef"
            :key="tag._id"
            v-bind="tag"
            :lang="lang"
            :class="{
              active: selectedTag._id === tag._id, 
              inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
            }"
            :tagLabel="index + 1"
            @mousedown.native.left.stop="handleDragStart($event, tag._id)"
            @mouseup.native.left.stop="handleDragEnd($event, tag._id)"
          ></tag-reference>

          <tag-rich
            v-for="tag in tagRich"
            :key="tag._id"
            v-bind="tag"
            :lang="lang"
            :dir="dir"
            :ref="tag._id"
            :class="{
              active: selectedTag._id === tag._id, 
              inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
            }"
            @mousedown.native.left.stop="handleDragStart($event, tag._id)"
            @mouseup.native.left.stop="handleDragEnd($event, tag._id)"
          ></tag-rich>

          <reference-tag-slider 
            v-if="referenceTagsSlider.length > 0"
            :key="referenceTagSliderKey"
            :lang="lang"
            :dir="dir"
            :isVertical="page.verticalSlider"
            :scaledAmount="scale"
            :tagRef="referenceTagsSlider"
          ></reference-tag-slider>

          <div ref="canvas" class="canvas" :class="{'no-image' : page.backgroundImage === ''}">
            <!-- <img class="tagBg" :class="{'hidden' : page.backgroundImage === ''}" :src="`${$store.state.assetsPath}${page.backgroundImage}`"> -->
            <img
              v-if="backgroundAssetType === 'image'"
              class="tagBg"
              :class="{'hidden' : page.backgroundImage === ''}"
              :src="`${$store.state.assetsPath}${page.backgroundImage}`"
            >
            <video
              v-if="backgroundAssetType === 'video'"
              class="tagBg"
              :class="{'hidden' : page.backgroundImage === ''}"
              :src="`${$store.state.assetsPath + page.backgroundImage}`"
              autoplay
              muted
              loop
            ></video>
          </div>
        </div>

        <tag-info-box v-model="selectedTag" :lang="lang" @remove="removeTag" :scale="scale"></tag-info-box>
      </div>
    </card>
  </div>
</template>

<script>
import InfoBox from '@/components/InfoBox'
import TagBasic from '@/components/TagBasic'
import TagBox from '@/components/TagBox'
import TagRich from '@/components/TagRich'
import TagReference from '@/components/TagReference'
import TagInfoBox from '@/components/TagInfoBox'
import ReferenceTagSlider from '@/components/ReferenceTagSlider'

export default {

  name: 'card-update-tags',

  components: {
    InfoBox,
    ReferenceTagSlider,
    TagBasic,
    TagBox,
    TagRich,
    TagReference,
    TagInfoBox
  },

  props: {
    dir: String,
    lang: String,
    page: Object,
    triggerSliderRefresh: Boolean
  },

  data() {
    return {
      draggingTag: {},

      isTagDraggable: false,
      isTagDragging: false,
      isTagSelected: false,

      modalContext: '',
      modalData: {
        asset: {},
        assetSelected: {
          content: {}
        }
      },

      scale: 1,

      shouldPopupTrigger: true,

      selectedTag: {}
    }
  },

  computed: {
    backgroundAssetType () {
      if (this.page.backgroundImage.split('.').pop() === 'mp4') {
        return 'video'
      } else {
        return 'image'
      }
    },

    canvasStyle() {
      if (this.scale) {
        return {
          ['font-size']: (20 * this.scale) + 'px',
          ['height']: (2000 * this.scale) + 'px',
        }
      }
      return {}
    },

    highestId() {
      let ids = [0];

      this.page.tags.forEach((tag) => {
        if (!isNaN(tag._id)) {
          ids.push(tag._id);
        }        
      });

      return (Math.max(...ids) + 1).toString();
    },

    referenceTagSliderKey() {
      return this.tagRef.length + '-' + this.page.verticalSlider + '-' + this.scale + '-' + this.triggerSliderRefresh
    },

    tagBasic: function() {
      return this.page.tags.filter(function(tag) {
        return tag.type == "basic";
      });
    },

    tagRich: function() {
      return this.page.tags.filter(function(tag) {
        return tag.type == "rich"
      })
    },

    tagBox: function() {
      return this.page.tags.filter(function(tag) {
        return tag.type == "box"
      })
    },

    tagRef: function() {
      let refTags = this.page.tags.filter(function(tag) {
        return tag.type == "reference"
      })

      if (this.dir === 'rtl') {
        return _.orderBy(refTags, ['order', 'pos.x'], ['desc'])
      } else {
        return _.orderBy(refTags, ['order', 'pos.x'], ['asc'])
      }
    },

    referenceTagsSlider () {
      let refTags = this.page.tags.filter(function(tag) {
        return tag.type == "reference"
      })

      refTags = _.orderBy(refTags, ['order', 'pos.x'], ['asc'])

      if (this.dir === 'rtl') {
        let startingIndex = refTags.length

        refTags.forEach((tag, index) => {
          if (!tag.hideTagReference) {
            tag.referenceIndex = startingIndex--
          }
        })
        return refTags
      } else {
        refTags.forEach((tag, index) => {
          if (!tag.hideTagReference) {
            tag.referenceIndex = index + 1
          }
        })
        return refTags
      }
    },
  },

  mounted() {
    window.addEventListener('resize', this.getScaledAmount)
    this.getScaledAmount()
  },

  methods: {
    getScaledAmount() {
      // default size based on 3040x2000 - scale = 1

      let canvasWidth = this.$refs.canvasWrapper.getBoundingClientRect().width
      let scaled = canvasWidth / 3040
      this.scale = scaled

      // close modal
      this.isTagSelected = false
    },

    handleDragStart(event, tagId) {
     
      this.isTagDraggable = true

      let tag = _.find(this.page.tags, tag => {
        return tag._id === tagId
      })

      this.draggingTag = tag
      this.selectedTag = tag
    },

    handleDrag(event) {
      // check if a tag is being dragged
      if (this.isTagDraggable) {
        this.isTagDragging = true
        this.shouldPopupTrigger = false
        this.isTagSelected = false

        const canvasWidth = this.$refs.canvas.offsetWidth
        const canvasHeight = this.$refs.canvas.offsetHeight

        // get tag element
        const draggingTagId = this.draggingTag._id

        let tag = _.find(this.page.tags, tag => {
          return tag._id === draggingTagId
        })

        const tagEl = document.getElementById(draggingTagId).getBoundingClientRect()
        const tagElWidth = tagEl.width
        const tagElHeight = tagEl.height

        tag.pos.x = Math.round(tag.pos.x + event.movementX / this.scale)
        tag.pos.y = Math.round(tag.pos.y + event.movementY / this.scale)

        if (tag.pos.x < 0) {
          tag.pos.x = 0
        }

        if (tag.pos.x > canvasWidth / this.scale) {
          tag.pos.x = Math.round(canvasWidth / this.scale)
        }

        if (tag.pos.y < 0) {
          tag.pos.y = 0
        }

        if (tag.pos.y > canvasHeight / this.scale) {
          tag.pos.y = Math.round(canvasHeight / this.scale)
        }
      }
    },

    handleDragEnd(event, tagId) {
      // end of drag or click
      let target = event.target.getAttribute('class')

      if (target === 'canvas-wrapper' || target === 'canvas' || target === 'canvas no-image') {
        this.isTagSelected = false
        this.selectedTag = {}
      }

      if (this.shouldPopupTrigger && !this.isTagDragging && tagId) {
        this.showPopupModal(tagId)
      }

      this.isTagDragging = false
      this.isTagDraggable = false
      this.shouldPopupTrigger = true
    },

    addTag() {
      let newTag = {
        _id: this.highestId,
        arrowDeg: '',
        description: {
          en: ''
        },
        distance: {
          en: ''
        },
        icon: '',
        image: '',
        label: {
          en: 'New Tag'
        },
        flip: false,
        pos: {
          x: 100,
          y: 100,
        },
        tailPosition: 'bottom-center',
        type: 'basic'
      };

      this.page.tags.push(newTag);
    },

    removeTag() {
      let selectedTagId = this.modalData.assetSelected.id;
      this.selectedTag = {}

      let remainingTags = this.page.tags.filter(function(tag) {
        if (tag._id != selectedTagId) {
          return tag
        }
      })

      this.page.tags = remainingTags
      this.isTagSelected = false
    },

    showPopupModal(tagId) {
      this.shouldPopupTrigger = true;
      this.isTagSelected = true;

      let tag = _.find(this.page.tags, tag => {
        return tag._id === tagId;
      });

      // get clicked element position
      const clickedElement = event.target;
      const height = clickedElement.getBoundingClientRect().height;
      const width = clickedElement.getBoundingClientRect().width;

      const modalWidth = 125;

      let modalLeft = (tag.pos.x * this.scale) - modalWidth + (width / 2)
      let modalTop = (tag.pos.y * this.scale) + height + 25

      // load up the data
      this.modalData.assetSelected = tag
      this.modalData.assetSelected.id = tag._id
      this.modalData.xPos = modalLeft
      this.modalData.yPos = modalTop
    },

    openAssetLibrary(context, assetSelected) {
      this.$emit('openAssetLibrary', context, assetSelected)
    },

    closeModal (event) {
      if (event.target.classList.contains('canvas')) {
        this.isTagSelected = false
        this.draggingTag = {}
      }
    }
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.getScaledAmount)
  }
}
</script>

<style lang="scss">
@import '@/styles/_mixins.scss';

.tags-block {
  position: relative;
}

.canvas {
  background-position: 50% 50%;
  background-size: cover;
  height: 100%;
  position: absolute;
  width: 100%;
  z-index: 1;

  &.no-image {
    background-image: linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%, #eee 100%), linear-gradient(45deg, #eee 25%, white 25%, white 75%, #eee 75%, #eee 100%);
    background-position: 0px 0px, 10px 10px;
    background-size: 20px 20px;
  }

  .tagBg {
    height: 100%;
    object-fit: cover;
    pointer-events: none;
    width: 100%;

    &.hidden {
      opacity: 0;
    }
  }
}

.canvas-wrapper {
  margin: 20px 0 5px;
  position: relative;
  width: 100%;

  @include breakpoint(1800px) {
  width: 80%;
  }
}

.card-update-tags {
  padding-right: 15px;
  padding-left: 15px;
  width: 100%;
}

.tag-anchor,
.tag-box,
.tag-rich,
.tag-reference {
  transition: opacity 200ms ease-in-out, box-shadow 200ms ease-in-out;
  z-index: 20;

  &.inactive {
    box-shadow: none;
    opacity: .7;
    z-index: 10;

    .tag-anchor__pos {

      .tag-rich,
      .tag-basic {
        box-shadow: none;
        opacity: .7;
        z-index: 10;
      }

      &:after {
        opacity: .7;
      }
    }
  }
}
</style>
